import {
  AlignLeft,
  Award,
  BookOpen,
  Bus,
  CalendarCheck,
  Clock,
  FlaskConical,
  Globe2,
  GraduationCap,
  Heart,
  HelpCircle,
  Images,
  Laptop,
  ListChecks,
  MapPin,
  Megaphone,
  Music,
  Shield,
  Sparkles,
  Star,
  Trophy,
  UsersRound,
  Utensils,
  type LucideIcon,
} from 'lucide-react'
import type {
  BlockType,
  EditorBlock,
  HighlightIcon,
  HomepageEditorData,
  HomepagePayload,
  PublicBlock,
  PublicHomepage,
} from './types'

interface BlockMeta {
  /** Name of the type in the editor (English source text, translated with t()). */
  label: string
  description: string
  /** Title shown when the school leaves the title empty. */
  defaultTitle: string
  icon: LucideIcon
}

export const BLOCK_META: Record<BlockType, BlockMeta> = {
  text: {
    label: 'Text',
    description: 'A title and a few paragraphs: presentation, history, project.',
    defaultTitle: 'About us',
    icon: AlignLeft,
  },
  highlights: {
    label: 'Highlights',
    description: 'Three to six strong points, each with an icon.',
    defaultTitle: 'Why choose us',
    icon: Sparkles,
  },
  programmes: {
    label: 'Classes offered',
    description: 'Your classes and séries, filled in automatically.',
    defaultTitle: 'Our classes',
    icon: GraduationCap,
  },
  admissions: {
    label: 'Admissions',
    description: 'Whether applications are open, the dates and a link to the online form.',
    defaultTitle: 'Admissions',
    icon: CalendarCheck,
  },
  list: {
    label: 'List',
    description: 'A checklist: supplies, uniform, documents, fees…',
    defaultTitle: 'What to prepare',
    icon: ListChecks,
  },
  announcements: {
    label: 'News',
    description: 'The latest announcements marked “Show on the public homepage”.',
    defaultTitle: 'News',
    icon: Megaphone,
  },
  faq: {
    label: 'Questions and answers',
    description: 'The questions families ask most often.',
    defaultTitle: 'Frequently asked questions',
    icon: HelpCircle,
  },
  gallery: {
    label: 'Gallery',
    description: 'Pictures of the school, with captions.',
    defaultTitle: 'Gallery',
    icon: Images,
  },
  contact: {
    label: 'Contact',
    description: 'Address, phone, email and opening hours from the school profile.',
    defaultTitle: 'Contact us',
    icon: MapPin,
  },
}

export const HIGHLIGHT_ICON_COMPONENTS: Record<HighlightIcon, LucideIcon> = {
  star: Star,
  award: Award,
  book: BookOpen,
  users: UsersRound,
  heart: Heart,
  shield: Shield,
  globe: Globe2,
  flask: FlaskConical,
  music: Music,
  trophy: Trophy,
  bus: Bus,
  meal: Utensils,
  clock: Clock,
  graduation: GraduationCap,
  sparkles: Sparkles,
  laptop: Laptop,
}

export const HIGHLIGHT_ICON_LABELS: Record<HighlightIcon, string> = {
  star: 'Star',
  award: 'Award',
  book: 'Book',
  users: 'People',
  heart: 'Heart',
  shield: 'Shield',
  globe: 'Globe',
  flask: 'Science',
  music: 'Music',
  trophy: 'Trophy',
  bus: 'Transport',
  meal: 'Meals',
  clock: 'Clock',
  graduation: 'Graduation',
  sparkles: 'Sparkles',
  laptop: 'Computer',
}

export const MAX_BLOCKS = 20

let counter = 0
/** A short id, unique within the page. */
export function newBlockId(taken: readonly string[] = []): string {
  let id: string
  do {
    counter += 1
    id = `b${Date.now().toString(36).slice(-4)}${counter.toString(36)}`
  } while (taken.includes(id))
  return id
}

/** A new block of a type, with starter content where the type needs some. */
export function newBlock(type: BlockType, taken: readonly string[] = []): EditorBlock {
  const block: EditorBlock = {
    id: newBlockId(taken),
    type,
    title: '',
    visible: true,
    body: '',
    items: [],
    highlights: [],
    faq: [],
    images: [],
    limit: null,
    mapUrl: '',
  }
  if (type === 'list') block.items = ['']
  if (type === 'faq') block.faq = [{ question: '', answer: '' }]
  if (type === 'highlights')
    block.highlights = [
      { title: '', text: '', icon: 'award' },
      { title: '', text: '', icon: 'users' },
      { title: '', text: '', icon: 'heart' },
    ]
  if (type === 'announcements') block.limit = 3
  return block
}

const clean = (s: string | null | undefined): string | null => {
  const v = s?.trim() ?? ''
  return v === '' ? null : v
}

export interface HomepageDraft {
  enabled: boolean
  heroTitle: string
  heroSubtitle: string
  cover: { fileId: number; url: string | null } | null
  accentColor: string
  blocks: EditorBlock[]
}

export function toDraft(data: HomepageEditorData): HomepageDraft {
  return {
    enabled: data.enabled,
    heroTitle: data.heroTitle ?? '',
    heroSubtitle: data.heroSubtitle ?? '',
    cover: data.cover,
    accentColor: data.accentColor ?? '',
    // Text fields are edited as strings; toPayload turns empty ones back into null.
    blocks: data.blocks.map((b) => ({
      ...b,
      title: b.title ?? '',
      body: b.body ?? '',
      mapUrl: b.mapUrl ?? '',
      highlights: b.highlights.map((h) => ({ ...h, text: h.text ?? '' })),
      images: b.images.map((i) => ({ ...i, caption: i.caption ?? '' })),
    })),
  }
}

export function toPayload(d: HomepageDraft): HomepagePayload {
  return {
    enabled: d.enabled,
    heroTitle: clean(d.heroTitle),
    heroSubtitle: clean(d.heroSubtitle),
    coverFileId: d.cover?.fileId ?? null,
    accentColor: clean(d.accentColor),
    blocks: d.blocks.map((b) => ({
      ...b,
      title: clean(b.title),
      body: clean(b.body),
      mapUrl: clean(b.mapUrl),
      items: b.items.map((i) => i.trim()).filter(Boolean),
      highlights: b.highlights.map((h) => ({ ...h, title: h.title.trim(), text: clean(h.text) })),
      faq: b.faq.map((f) => ({ question: f.question.trim(), answer: f.answer.trim() })),
      images: b.images.map((i) => ({ fileId: i.fileId, caption: clean(i.caption) })),
    })),
  }
}

/**
 * What visitors would see with the draft: the same rules as the API (visible blocks only,
 * automatic blocks filled from the school's data, empty automatic blocks left out).
 */
export function previewPage(d: HomepageDraft, context: HomepageEditorData['context']): PublicHomepage {
  const blocks: PublicBlock[] = []
  for (const b of d.blocks) {
    if (!b.visible) continue
    const base = { id: b.id, title: clean(b.title) }
    switch (b.type) {
      case 'text':
        blocks.push({ ...base, type: 'text', body: clean(b.body) })
        break
      case 'admissions':
        blocks.push({ ...base, type: 'admissions', body: clean(b.body) })
        break
      case 'list':
        blocks.push({
          ...base,
          type: 'list',
          body: clean(b.body),
          items: b.items.map((i) => i.trim()).filter(Boolean),
        })
        break
      case 'highlights':
        blocks.push({ ...base, type: 'highlights', highlights: b.highlights })
        break
      case 'faq':
        blocks.push({ ...base, type: 'faq', faq: b.faq })
        break
      case 'contact':
        blocks.push({ ...base, type: 'contact', body: clean(b.body), mapUrl: clean(b.mapUrl) })
        break
      case 'gallery': {
        const images = b.images.flatMap((i) => (i.url ? [{ url: i.url, caption: clean(i.caption) }] : []))
        if (images.length) blocks.push({ ...base, type: 'gallery', images })
        break
      }
      case 'programmes':
        if (context.classes.length)
          blocks.push({ ...base, type: 'programmes', body: clean(b.body), classes: context.classes })
        break
      case 'announcements': {
        const items = context.announcements.slice(0, Math.min(6, Math.max(1, b.limit ?? 3)))
        if (items.length) blocks.push({ ...base, type: 'announcements', items })
        break
      }
    }
  }
  return {
    enabled: true,
    school: context.school,
    heroTitle: clean(d.heroTitle),
    heroSubtitle: clean(d.heroSubtitle),
    coverUrl: d.cover?.url ?? null,
    accentColor: /^#[0-9a-f]{6}$/i.test(d.accentColor) ? d.accentColor : null,
    contact: context.contact,
    blocks,
    updatedAt: null,
  }
}

/** Short text for the page's meta description. */
export function describePage(page: PublicHomepage): string {
  const text =
    page.heroSubtitle ??
    page.blocks.flatMap((b) => (b.type === 'text' && b.body ? [b.body] : []))[0] ??
    page.school.motto ??
    page.school.name
  const flat = text.replace(/\s+/g, ' ').trim()
  return flat.length > 160 ? `${flat.slice(0, 157).trimEnd()}…` : flat
}
